<script setup>
import { useAlert } from 'dashboard/composables';
import { computed, onBeforeMount, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStoreGetters, useStore } from 'dashboard/composables/store';
import { picoSearch } from '@scmmishra/pico-search';

import AddAutomationText from './AddAutomationText.vue';
import EditAutomationText from './EditAutomationText.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import {
  BaseTable,
  BaseTableRow,
  BaseTableCell,
} from 'dashboard/components-next/table';

const getters = useStoreGetters();
const store = useStore();
const { t } = useI18n();

const loading = ref({});
const showAddPopup = ref(false);
const showEditPopup = ref(false);
const showDeleteConfirmationPopup = ref(false);
const selectedItem = ref({});
const searchQuery = ref('');

const records = computed(
  () => getters['automationTexts/getAutomationTexts'].value
);

const filteredRecords = computed(() => {
  const query = searchQuery.value.trim();
  if (!query) return records.value;
  return picoSearch(records.value, query, [
    { name: 'title', weight: 4 },
    'body',
  ]);
});
const uiFlags = computed(() => getters['automationTexts/getUIFlags'].value);

const openAddPopup = () => {
  showAddPopup.value = true;
};
const hideAddPopup = () => {
  showAddPopup.value = false;
};

const openEditPopup = response => {
  showEditPopup.value = true;
  selectedItem.value = response;
};
const hideEditPopup = () => {
  showEditPopup.value = false;
};

const openDeletePopup = response => {
  showDeleteConfirmationPopup.value = true;
  selectedItem.value = response;
};
const closeDeletePopup = () => {
  showDeleteConfirmationPopup.value = false;
};

const deleteItem = async id => {
  try {
    await store.dispatch('automationTexts/delete', id);
    useAlert(t('AUTOMATION_TEXT_MGMT.DELETE.API.SUCCESS_MESSAGE'));
  } catch (error) {
    const errorMessage =
      error?.message || t('AUTOMATION_TEXT_MGMT.DELETE.API.ERROR_MESSAGE');
    useAlert(errorMessage);
  } finally {
    loading.value[selectedItem.value.id] = false;
  }
};

const confirmDeletion = () => {
  loading.value[selectedItem.value.id] = true;
  closeDeletePopup();
  deleteItem(selectedItem.value.id);
};

const tableHeaders = computed(() => {
  return [
    t('AUTOMATION_TEXT_MGMT.LIST.TABLE_HEADER.TITLE'),
    t('AUTOMATION_TEXT_MGMT.LIST.TABLE_HEADER.BODY'),
    t('AUTOMATION_TEXT_MGMT.LIST.TABLE_HEADER.BUTTONS'),
    t('AUTOMATION_TEXT_MGMT.LIST.TABLE_HEADER.STATUS'),
    t('AUTOMATION_TEXT_MGMT.LIST.TABLE_HEADER.ACTION'),
  ];
});

onBeforeMount(() => {
  store.dispatch('automationTexts/get');
});
</script>

<template>
  <SettingsLayout
    :is-loading="uiFlags.isFetching"
    :loading-message="$t('AUTOMATION_TEXT_MGMT.LOADING')"
    :no-records-found="!records.length"
    :no-records-message="$t('AUTOMATION_TEXT_MGMT.LIST.404')"
  >
    <template #header>
      <BaseSettingsHeader
        v-model:search-query="searchQuery"
        :title="$t('AUTOMATION_TEXT_MGMT.HEADER')"
        :description="$t('AUTOMATION_TEXT_MGMT.DESCRIPTION')"
        :search-placeholder="$t('AUTOMATION_TEXT_MGMT.SEARCH_PLACEHOLDER')"
        feature-name="automation_texts"
      >
        <template v-if="records?.length" #count>
          <span class="text-body-main text-n-slate-11">
            {{ records.length }}
          </span>
        </template>
        <template #actions>
          <Button
            :label="$t('AUTOMATION_TEXT_MGMT.HEADER_BTN_TXT')"
            size="sm"
            @click="openAddPopup"
          />
        </template>
      </BaseSettingsHeader>
    </template>
    <template #body>
      <BaseTable
        :headers="tableHeaders"
        :items="filteredRecords"
        :no-data-message="
          searchQuery
            ? $t('AUTOMATION_TEXT_MGMT.NO_RESULTS')
            : $t('AUTOMATION_TEXT_MGMT.LIST.404')
        "
      >
        <template #row="{ items }">
          <BaseTableRow v-for="item in items" :key="item.id" :item="item">
            <template #default>
              <BaseTableCell>
                <span class="text-body-main text-n-slate-12 font-medium">
                  {{ item.title }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="text-body-main text-n-slate-11 truncate max-w-xs block"
                >
                  {{ item.body }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span class="text-body-main text-n-slate-11">
                  {{ item.buttons?.length || 0 }}
                </span>
              </BaseTableCell>

              <BaseTableCell>
                <span
                  class="px-2 py-0.5 rounded-full text-xs font-medium"
                  :class="
                    item.active
                      ? 'bg-n-teal-3 text-n-teal-11'
                      : 'bg-n-slate-3 text-n-slate-11'
                  "
                >
                  {{
                    item.active
                      ? $t(
                          'AUTOMATION_TEXT_MGMT.LIST.TABLE_HEADER.STATUS_ACTIVE'
                        )
                      : $t(
                          'AUTOMATION_TEXT_MGMT.LIST.TABLE_HEADER.STATUS_INACTIVE'
                        )
                  }}
                </span>
              </BaseTableCell>

              <BaseTableCell align="end">
                <div class="flex gap-3 justify-end flex-shrink-0">
                  <Button
                    v-tooltip.top="$t('AUTOMATION_TEXT_MGMT.FORM.EDIT')"
                    icon="i-woot-edit-pen"
                    slate
                    sm
                    :is-loading="loading[item.id]"
                    @click="openEditPopup(item)"
                  />
                  <Button
                    v-tooltip.top="$t('AUTOMATION_TEXT_MGMT.FORM.DELETE')"
                    icon="i-woot-bin"
                    slate
                    sm
                    class="hover:enabled:text-n-ruby-11 hover:enabled:bg-n-ruby-2"
                    :is-loading="loading[item.id]"
                    @click="openDeletePopup(item)"
                  />
                </div>
              </BaseTableCell>
            </template>
          </BaseTableRow>
        </template>
      </BaseTable>
    </template>

    <woot-modal v-model:show="showAddPopup" :on-close="hideAddPopup">
      <AddAutomationText @close="hideAddPopup" />
    </woot-modal>

    <woot-modal v-model:show="showEditPopup" :on-close="hideEditPopup">
      <EditAutomationText
        :selected-response="selectedItem"
        @close="hideEditPopup"
      />
    </woot-modal>

    <woot-delete-modal
      v-model:show="showDeleteConfirmationPopup"
      :on-close="closeDeletePopup"
      :on-confirm="confirmDeletion"
      :title="$t('AUTOMATION_TEXT_MGMT.DELETE.CONFIRM.TITLE')"
      :message="$t('AUTOMATION_TEXT_MGMT.DELETE.CONFIRM.MESSAGE')"
      :message-value="` ${selectedItem.title}?`"
      :confirm-text="$t('AUTOMATION_TEXT_MGMT.DELETE.CONFIRM.YES')"
      :reject-text="$t('AUTOMATION_TEXT_MGMT.DELETE.CONFIRM.NO')"
    />
  </SettingsLayout>
</template>
